(function () {
  "use strict";

  if (customElements.get("swiper-slider")) return;
  if (!window.EssenceBaseComponent) return;

  /** Único breakpoint do tema: celular < 768px ≤ computador. */
  var DESKTOP_BP = 768;

  function parseBoolean(value, fallback) {
    if (value === "true") return true;
    if (value === "false") return false;
    return fallback;
  }

  function parseNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /** Atributos opcionais lidos via dataset: [datasetKey, optionKey, parser, fallback]. */
  var OPTIONAL_ATTRS = [
    ["speed", "speed", parseNumber, 300],
    ["centeredSlides", "centeredSlides", parseBoolean, false],
    ["allowTouchMove", "allowTouchMove", parseBoolean, true],
    ["preventClicks", "preventClicks", parseBoolean, true],
    ["preventClicksPropagation", "preventClicksPropagation", parseBoolean, true],
  ];

  /**
   * Layout do carrossel: fonte única são as 4 variáveis CSS do host
   * (--slides-per-view-mobile/-desktop, --gap-mobile/-desktop), as mesmas que o
   * theme.css usa para reservar o espaço antes do Swiper iniciar. Os gaps usam os
   * tokens --space-* (rem) e o Swiper precisa de px.
   * Exposto em theme.sliderLayout para outros carrosséis (ex.: video-reels.js).
   */
  function readLayout(el) {
    var style = getComputedStyle(el);
    var rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    function read(name) {
      var value = style.getPropertyValue(name).trim();
      return value.endsWith("rem") ? parseFloat(value) * rootFontSize : parseFloat(value);
    }
    return {
      mobile: { slidesPerView: read("--slides-per-view-mobile"), spaceBetween: read("--gap-mobile") },
      desktop: { slidesPerView: read("--slides-per-view-desktop"), spaceBetween: read("--gap-desktop") },
    };
  }

  theme.sliderLayout = readLayout;

  class SwiperSlider extends window.EssenceBaseComponent {
    init() {
      var slidesCount = this.querySelectorAll(".swiper-slide").length;
      if (slidesCount < 1) {
        if (!this.__tdWaitSlides) {
          var self = this;
          this.__tdWaitSlides = new MutationObserver(function () {
            if (self.querySelectorAll(".swiper-slide").length < 1) return;
            self.__tdWaitSlides.disconnect();
            self.__tdWaitSlides = null;
            self.destroy?.();
            self.__tdInitialized = false;
            self.initOnce();
          });
          this.__tdWaitSlides.observe(this, { childList: true, subtree: true });
        }
        return;
      }

      var autoplay = parseBoolean(this.dataset.autoplay, false);
      var autoplaySpeed = parseNumber(this.dataset.autoplaySpeed, 8);
      var autoplayObj = false;
      if (autoplay) {
        autoplayObj = { delay: autoplaySpeed * 1000 };
        if (this.dataset.autoplayDisableOnInteraction === "false") {
          autoplayObj.disableOnInteraction = false;
        } else if (this.dataset.autoplayDisableOnInteraction === "true") {
          autoplayObj.disableOnInteraction = true;
        }
        if (parseBoolean(this.dataset.autoplayPauseOnMouseEnter, false)) {
          autoplayObj.pauseOnMouseEnter = true;
        }
      }
      var effect = this.dataset.effect || "slide";
      var layout = readLayout(this);
      var maxPerView = Math.max(layout.mobile.slidesPerView, layout.desktop.slidesPerView);
      var canLoop = parseBoolean(this.dataset.loop, true) && slidesCount >= Math.ceil(maxPerView) + 1;

      var paginationEl = this.querySelector(".swiper-pagination");
      var navRoot = this;
      if (this.dataset.navigationScope) {
        var scoped = this.closest(this.dataset.navigationScope);
        if (scoped) navRoot = scoped;
      }
      var nextEl =
        navRoot.querySelector(".swiper-button-next") ||
        this.querySelector(".swiper-button-next") ||
        this.querySelector(".swiper-nav-next");
      var prevEl =
        navRoot.querySelector(".swiper-button-prev") ||
        this.querySelector(".swiper-button-prev") ||
        this.querySelector(".swiper-nav-prev");
      var scrollbarEl = this.querySelector(".swiper-scrollbar");

      var breakpoints = {};
      breakpoints[0] = layout.mobile;
      breakpoints[DESKTOP_BP] = layout.desktop;

      var options = {
        breakpoints: breakpoints,
        slidesPerGroup: 1,
        loop: canLoop,
        grabCursor: parseBoolean(this.dataset.grabCursor, true),
        effect: effect,
        autoplay: autoplayObj,
        pagination:
          parseBoolean(this.dataset.pagination, !!paginationEl) && paginationEl
            ? { el: paginationEl, clickable: true }
            : false,
        navigation:
          parseBoolean(this.dataset.navigation, !!(nextEl && prevEl)) && nextEl && prevEl
            ? { nextEl: nextEl, prevEl: prevEl }
            : false,
        scrollbar:
          parseBoolean(this.dataset.scrollbar, !!scrollbarEl) && scrollbarEl
            ? { el: scrollbarEl }
            : false,
      };

      // O módulo a11y do Swiper sobrescreve o aria-label das setas com "Next slide"/"Previous slide":
      // repassa o rótulo traduzido que o snippet slider-arrows já colocou no botão.
      if (options.navigation) {
        var prevLabel = prevEl.getAttribute("aria-label");
        var nextLabel = nextEl.getAttribute("aria-label");
        var a11y = {};
        if (prevLabel) a11y.prevSlideMessage = prevLabel;
        if (nextLabel) a11y.nextSlideMessage = nextLabel;
        options.a11y = a11y;
      }

      OPTIONAL_ATTRS.forEach(function (entry) {
        var dsKey = entry[0];
        var optKey = entry[1];
        var parser = entry[2];
        var fallback = entry[3];
        if (this.dataset[dsKey] === undefined) return;
        options[optKey] = parser(this.dataset[dsKey], fallback);
      }, this);

      // destroy(true, true) apaga o atributo style do host, onde ficam as 4 variáveis.
      // Guardado para restaurar: ao mover a seção no editor, o MESMO elemento é
      // desconectado e reconectado, sem HTML novo do servidor.
      this.__hostStyle = this.getAttribute("style");
      this.swiperInstance = new Swiper(this, options);

      if (parseBoolean(this.dataset.applyThemeAnimation, false)) {
        var applyAnim = function () {
          if (theme && theme.utilities && theme.utilities.applyAnimation) {
            theme.utilities.applyAnimation();
          }
        };
        this.swiperInstance.on("afterInit", applyAnim);
        this.swiperInstance.on("loopFix", applyAnim);
      }

      if (parseBoolean(this.dataset.stopAutoplayOnInteraction, false)) {
        this.swiperInstance.on("slideChange", function (swiper) {
          if (swiper.autoplay && (swiper.touches.diff || swiper.clicked)) {
            swiper.autoplay.stop();
          }
        });
      }
    }

    onEditorBlockSelect(event) {
      if (!this.swiperInstance || !event.detail || !event.detail.blockId) return;

      var blockId = event.detail.blockId;
      var slide = this.querySelector(
        '.swiper-slide[data-block-id="' + blockId + '"], [id="Slide-' + blockId + '"]'
      );
      if (!slide) return;

      if (this.swiperInstance.autoplay.running) this.swiperInstance.autoplay.stop();

      if (this.swiperInstance.params.loop) {
        this.swiperInstance.slideToLoop(Number(slide.dataset.swiperSlideIndex), 500);
      } else {
        this.swiperInstance.slideTo(this.swiperInstance.slides.indexOf(slide), 500);
      }
    }

    onEditorBlockDeselect() {
      if (!this.swiperInstance) return;
      if (parseBoolean(this.dataset.autoplay, false)) {
        this.swiperInstance.autoplay.start();
      }
    }

    destroy() {
      if (this.__tdWaitSlides) {
        this.__tdWaitSlides.disconnect();
        this.__tdWaitSlides = null;
      }
      if (this.swiperInstance) this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
      if (this.__hostStyle != null) this.setAttribute("style", this.__hostStyle);
    }
  }

  customElements.define("swiper-slider", SwiperSlider);
})();
